import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { apiPathV4 } from '../config';
import { getAuthToken } from '../utils/authUtils';
import {
  clearFeatureFlagsCache,
  readFeatureFlagsCache,
  writeFeatureFlagsCache,
  type CachedFeatureFlag,
} from '../utils/featureFlagsCache';
import { useAppAuth } from './AppContext';

type ResolvedFeature = CachedFeatureFlag;

type FeatureFlagsResponse = {
  features?: ResolvedFeature[];
};

/** keepOnError: a failed refresh keeps the flags already loaded.
 *  background: do not flip isLoading (used after SWR hydrate). */
type RefreshOptions = { keepOnError?: boolean; background?: boolean };

type FeatureFlagsContextType = {
  features: ResolvedFeature[];
  isLoading: boolean;
  refreshFeatureFlags: (options?: RefreshOptions) => Promise<void>;
  hasFlag: (flagKey: string) => boolean;
};

const FeatureFlagsContext = createContext<FeatureFlagsContextType | null>(null);

export function useFeatureFlags(): FeatureFlagsContextType {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  return context;
}

export function FeatureFlagsProvider({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  const {
    isAuthenticated,
    isLoading: isAuthLoading,
    user,
    getIdTokenClaims,
    getAccessTokenSilently,
  } = useAppAuth();
  const [features, setFeatures] = useState<ResolvedFeature[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const refreshFeatureFlags = useCallback(async (options?: RefreshOptions) => {
    if (!isAuthenticated) {
      setFeatures([]);
      setIsLoading(false);
      return;
    }

    if (!options?.background) {
      setIsLoading(true);
    }
    try {
      let token = await getAuthToken(getIdTokenClaims);
      if (!token) {
        token = await getAccessTokenSilently();
      }

      if (!token) {
        // Background SWR refresh must not wipe hydrated flags on a transient
        // token miss (Auth0 race). Explicit refresh without keepOnError still clears.
        if (!options?.keepOnError) {
          setFeatures([]);
        }
        return;
      }

      const response = await fetch(`${apiPathV4()}/feature-flags/me`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        // Auth failures must drop cached gates even during background refresh.
        if (response.status === 401 || response.status === 403) {
          setFeatures([]);
          clearFeatureFlagsCache();
          return;
        }
        throw new Error(`Failed to resolve feature flags (${response.status})`);
      }

      const data = (await response.json()) as FeatureFlagsResponse;
      const nextFeatures = Array.isArray(data.features) ? data.features : [];
      setFeatures(nextFeatures);

      const userSub = user?.sub;
      if (userSub) {
        writeFeatureFlagsCache(userSub, user?.ept_app_metadata?.account_id, nextFeatures);
      }
    } catch (error) {
      console.error('[FEATURE FLAGS] Failed to load feature flags:', error);
      if (!options?.keepOnError) {
        setFeatures([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    getAccessTokenSilently,
    getIdTokenClaims,
    isAuthenticated,
    user?.ept_app_metadata?.account_id,
    user?.sub,
  ]);

  useEffect(() => {
    if (isAuthLoading) {
      return;
    }

    if (!isAuthenticated) {
      setFeatures([]);
      setIsLoading(false);
      return;
    }

    const userSub = user?.sub;
    const accountId = user?.ept_app_metadata?.account_id;
    const cached = userSub ? readFeatureFlagsCache(userSub, accountId) : null;

    if (cached !== null) {
      // Hydrate immediately so HomeRoute can redirect without waiting on network.
      setFeatures(cached);
      setIsLoading(false);
      void refreshFeatureFlags({ background: true, keepOnError: true });
      return;
    }

    void refreshFeatureFlags();
  }, [
    isAuthLoading,
    isAuthenticated,
    refreshFeatureFlags,
    user?.sub,
    user?.ept_app_metadata?.account_id,
    user?.ept_roles,
  ]);

  const activeFlagKeys = useMemo(() => {
    return new Set(
      features
        .map(feature => feature.flag_key?.trim())
        .filter((flagKey): flagKey is string => Boolean(flagKey))
    );
  }, [features]);

  const hasFlag = useCallback(
    (flagKey: string): boolean => {
      return activeFlagKeys.has(flagKey.trim());
    },
    [activeFlagKeys]
  );

  const value = useMemo(
    () => ({
      features,
      isLoading,
      refreshFeatureFlags,
      hasFlag,
    }),
    [features, isLoading, refreshFeatureFlags, hasFlag]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}
